<template>
    <div class="step-content">
        <slot name="title">
            <StepTitle
                :active="active"
                :error="error"
                :complete="complete"
                :title="$t(`email.broadcast.editor.sectionTitle.subject`)"
            />
        </slot>

        <div class="main-content">
            <div v-if="active" class="input-fields">
                <AdvancedInput
                    :value="subject"
                    data-qa="subject-field-advanced"
                    name="subject"
                    required
                    :autocomplete="$options.SUBJECT_AUTOCOMPLETE"
                    :error-text="subjectErrorText"
                    :invalid="!isSubjectValid"
                    :label="$t('email.broadcast.steps.subject.subjectLabel')"
                    :maxlength="subjectLengthHardLimit"
                    :submitted="!isSubjectValid"
                    @input="handleSubjectChange"
                >
                    <template #toolbar="{ insertTextIntoInput }">
                        <EmailBroadcastInputToolbar
                            :insert-text-into-input="insertTextIntoInput"
                            :merge-field-categories="mergeFields"
                        />
                    </template>
                </AdvancedInput>

                <ContentRiskMessages
                    v-if="hasSubjectRisk"
                    :collapsible="subjectRiskMessages.length > 1"
                    :quality="subjectRiskQuality"
                    :messages="subjectRiskMessages"
                    dismissible
                    class="subject-risk-messages"
                    data-qa="subject-risk-messages"
                >
                    <div>
                        <span>{{ $tc('email.broadcast.steps.subject.contentRisk.common.description1', subjectRiskMessages.length) }} </span>
                        <strong>{{ $tc('email.broadcast.steps.subject.contentRisk.common.numberOfMessages', subjectRiskMessages.length, { messages: subjectRiskMessages.length }) }} </strong>
                        <span>{{ $t('email.broadcast.steps.subject.contentRisk.subject.description2') }} </span>
                        <span class="cta">{{ $tc('email.broadcast.steps.subject.contentRisk.common.cta', subjectRiskMessages.length) }} </span>
                        <span>{{ $t('email.broadcast.steps.subject.contentRisk.common.description3') }}</span>
                    </div>
                </ContentRiskMessages>

                <AdvancedInput
                    v-if="!hidePreview"
                    v-model="previewText"
                    name="preview"
                    :autocomplete="$options.PREVIEW_AUTOCOMPLETE"
                    :invalid="hasPreviewError"
                    :error-text="previewErrorText"
                    :help-text="!hasPreviewError ? $t('email.broadcast.steps.subject.previewHelp') : null"
                    :label="$t('email.broadcast.steps.subject.previewLabel')"
                    :maxlength="previewLengthHardLimit"
                    :submitted="hasPreviewError"
                    data-qa="preview-field"
                >
                    <template #toolbar="{ insertTextIntoInput }">
                        <EmailBroadcastInputToolbar
                            :insert-text-into-input="insertTextIntoInput"
                            :merge-field-categories="mergeFields"
                        />
                    </template>
                </AdvancedInput>

                <ContentRiskMessages
                    v-if="hasPreviewRisk"
                    :collapsible="previewRiskMessages.length > 1"
                    :quality="previewRiskQuality"
                    :messages="previewRiskMessages"
                    dismissible
                    class="subject-risk-messages"
                    data-qa="preview-risk-messages"
                >
                    <div>
                        <span>{{ $tc('email.broadcast.steps.subject.contentRisk.common.description1', previewRiskMessages.length) }} </span>
                        <strong>{{ $tc('email.broadcast.steps.subject.contentRisk.common.numberOfMessages', previewRiskMessages.length, { messages: previewRiskMessages.length }) }} </strong>
                        <span>{{ $t('email.broadcast.steps.subject.contentRisk.preview.description2') }} </span>
                        <span class="cta">{{ $tc('email.broadcast.steps.subject.contentRisk.common.cta', previewRiskMessages.length) }} </span>
                        <span>{{ $t('email.broadcast.steps.subject.contentRisk.common.description3') }}</span>
                    </div>
                </ContentRiskMessages>

                <div class="action-buttons">
                    <DsOutlineButton
                        class="save"
                        data-qa="save-button"
                        :loading="loadingSubjectRisk || loadingPreviewRisk"
                        @click="save"
                    >
                        {{ $t('global.save') }}
                    </DsOutlineButton>

                    <DsTextButton
                        class="cancel"
                        @click.stop="cancel"
                    >
                        {{ $t('global.cancel') }}
                    </DsTextButton>
                </div>
            </div>

            <EmailBroadcastStepOverview v-else :sections="overviewSections" />
        </div>

        <slot name="action" :actionText="$t('email.broadcast.steps.subject.actionText')" />
    </div>
</template>

<script>
import debounce from 'lodash.debounce';
import { INPUT_DEBOUNCE_DELAY } from '@/components/shared/constants/timing.constants';
import AdvancedInput from '@/components/shared/AdvancedInput.vue';
import EmailBroadcastInputToolbar from '../EmailBroadcastInputToolbar.vue';
import ContentRiskMessages from '@/components/unlayer/ContentRiskMessages';
import { CONTENT_RISK_QUALITY } from '@/components/unlayer/content-risk.constants';
import EmailBroadcastStepOverview from './EmailBroadcastStepOverview';
import { EMAIL_BROADCAST_PREVIEW, EMAIL_BROADCAST_SUBJECT } from '../../constants/emailBroadcast.constants';
import { STEP_ERRORS } from '@/components/unlayer/constants/editor.constants';
import StepTitle from '@/components/unlayer/UnlayerEmailStepTitle.vue';

const INPUT_LENGTH_HARD_LIMIT = 500;

export default {
    name: 'UnifiedFlowEmailSubjectStep',

    CONTENT_RISK_QUALITY,
    SUBJECT_AUTOCOMPLETE: EMAIL_BROADCAST_SUBJECT.autocomplete,
    PREVIEW_AUTOCOMPLETE: EMAIL_BROADCAST_PREVIEW.autocomplete,

    components: {
        StepTitle,
        EmailBroadcastStepOverview,
        ContentRiskMessages,
        AdvancedInput,
        EmailBroadcastInputToolbar,
    },

    props: {
        active: Boolean,
        error: Boolean,
        complete: Boolean,
        savedSubject: {
            type: String,
            default: ''
        },
        savedPreviewText: {
            type: String,
            default: '',
        },
        debounceDelay: {
            type: Number,
            default: INPUT_DEBOUNCE_DELAY,
        },

        fetchSubjectRisks: {
            type: Function,
            default: async (x) => x,
        },
        loadingSubjectRisk: Boolean,
        subjectRiskSendable: Boolean,
        subjectRiskQuality: String,
        subjectRiskMessages: {
            type: Array,
            default: () => [],
        },

        fetchPreviewRisks: {
            type: Function,
            default: async (x) => x,
        },
        loadingPreviewRisk: Boolean,
        previewRiskSendable: Boolean,
        previewRiskQuality: {
            type: String,
            default: null,
        },
        previewRiskMessages: {
            type: Array,
            default: () => [],
        },

        previewSpamCheckEnabled: Boolean,
        showErrors: Boolean,
        mergeFields: {
            type: Array,
        },
        hidePreview: Boolean,
    },

    data() {
        return {
            hasSubjectError: false,
            previewText: '',
            subject: '',
        };
    },

    mounted() {
        if (this.debounceDelay > 0) {
            this.callFetchSubjectRisks = debounce(this.callFetchSubjectRisks, this.debounceDelay);
            this.callFetchPreviewRisks = debounce(this.callFetchPreviewRisks, this.debounceDelay);
        }

        if (this.hasSubjectLimitExceeded || this.hasPreviewLimitExceeded) {
            this.$emit('error', STEP_ERRORS.SUBJECT_OR_PREVIEW_LIMIT_EXCEEDED);
        }
    },

    watch: {
        savedSubject: {
            handler() {
                this.syncData();
            },
            immediate: true,
        },

        savedPreviewText: {
            handler() {
                this.syncData();
            },
            immediate: true,
        },

        previewText() {
            this.handlePreviewChange();
        },

        showErrors(val) {
            if (val) this.validate();
        },
    },

    computed: {
        subjectLengthHardLimit() {
            return INPUT_LENGTH_HARD_LIMIT;
        },

        previewLengthHardLimit() {
            return INPUT_LENGTH_HARD_LIMIT;
        },

        hasSubjectRisk() {
            return this.subjectRiskMessages.length > 0 && this.subjectRiskQuality !== this.$options.CONTENT_RISK_QUALITY.OK;
        },

        hasPreviewRisk() {
            return this.previewRiskMessages.length > 0 && !this.previewRiskQuality !== this.$options.CONTENT_RISK_QUALITY.OK;
        },

        hasPreviewError() {
            if (this.previewText?.length === 0) return false;
            if (this.hasPreviewLimitExceeded) return true;

            if (this.previewSpamCheckEnabled) {
                return !this.previewRiskSendable;
            }

            return false;
        },

        hasSubjectLimitExceeded() {
            return this.subject?.length > EMAIL_BROADCAST_SUBJECT.maxlength;
        },

        hasPreviewLimitExceeded() {
            return this.previewText?.length > EMAIL_BROADCAST_PREVIEW.maxlength;
        },

        subjectErrorText() {
            if (!this.subject || this.subject?.trim().length === 0) return this.$t('email.broadcast.steps.subject.subjectError');

            if (this.hasSubjectLimitExceeded) {
                return `${this.$t('unlayerEmail.unifiedFlow.subjectStep.limitExceededError')} (${this.subject.length}/${EMAIL_BROADCAST_SUBJECT.maxlength})`;
            }

            return '';
        },

        previewErrorText() {
            if (this.hasPreviewLimitExceeded) {
                return `${this.$t('unlayerEmail.unifiedFlow.subjectStep.limitExceededError')} (${this.previewText.length}/${EMAIL_BROADCAST_PREVIEW.maxlength})`;
            }

            return '';
        },

        overviewSections() {
            return !this.hidePreview ? [{
                content: this.subject,
                placeholder: this.$t('email.broadcast.steps.subject.placeholders.subject'),
                title: this.$t('global.subject'),
            }, {
                content: this.previewText,
                placeholder: this.$t('email.broadcast.steps.subject.placeholders.preview'),
                title: this.$t('global.preview'),
            }] : [{
                content: this.subject,
                placeholder: this.$t('email.broadcast.steps.subject.placeholders.subject'),
                title: this.$t('global.subject'),
            }];
        },

        isSubjectValid() {
            return !this.hasSubjectError && this.subjectRiskSendable && !this.hasSubjectLimitExceeded;
        },
    },

    methods: {
        callFetchSubjectRisks(...args) {
            return this.fetchSubjectRisks(...args);
        },

        callFetchPreviewRisks(...args) {
            this.fetchPreviewRisks(...args);
        },

        cancel() {
            this.hasSubjectError = false;
            this.syncData();
            this.$emit('cancel');
        },

        handleSubjectChange(text) {
            this.hasSubjectError = false;
            this.subject = text;

            this.callFetchSubjectRisks(text);
        },

        handlePreviewChange() {
            if (!this.previewSpamCheckEnabled) return;

            this.callFetchPreviewRisks(this.previewText?.trim());
        },

        save() {
            this.validate();

            if (this.isSubjectValid && !this.hasPreviewError) {
                const { subject, previewText } = this;

                this.$emit('save', {
                    subject,
                    previewText,
                });
            }
        },

        syncData() {
            this.subject = this.savedSubject;
            this.previewText = this.savedPreviewText;
        },

        validate() {
             this.hasSubjectError = !(this.subject?.length > 0
                    && !this.hasSubjectLimitExceeded
                    && !this.hasPreviewLimitExceeded
                    && this.subjectRiskSendable);
        },
    },
};
</script>

<style lang="scss" scoped>
@import './steps';

.subject-risk-messages {
    margin-bottom: $spacing-200;
}

.input-fields {
    --mergable-input-margin-bottom: #{$spacing-200};
}

.action-buttons {
    display: flex;
    align-items: flex-start;
}
</style>
